import HTTPService            from "@/js/Shared/Services/HTTP/HTTPService.js";
import SAPOMailConfig         from "@/SAPOMailConfig";
import EventUpload            from "@/js/Shared/Events/EventUpload";
import HTTPResponseEntity     from "@/js/Shared/Services/HTTP/Entities/HTTPResponseEntity";
import MessageAttachmentsItem from "@/js/Mailboxes/Entities/Message/MessageAttachmentsItem";

export {MeoCloudHTTPService as default};

/**
* Compose HTTP Service
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class MeoCloudHTTPService {
  /**
   * Compose Enpoint
   *
   * @public
   *
   * @type {String}
   *
   * @since 7.0.0
   */
  endpoint = SAPOMailConfig.client.endpoints.imp_services + '/cloud';

  /**
   * Calls meocloud api to authorize the usage of the service
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async authorize() {
    let request = {
      op: 'cloud',
    }

    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Calls meocloud api to remove the authorization the usage of the service
   *
   * @public
   *
   * @since 7.0.0
   *
   * @returns {Promise<HTTPResponseEntity>}
   */
  async removeAuthorization() {
    let request = {
      op: 'disableCloud',
    }

    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Returns file list on a given folder
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} path folder path
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async browse(path) {
    let request = {
      op  : 'exploreList',
      data: path,
    }

    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Creates a new folder on a given path
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} path        folder path
   * @param {String} folder_name folder name to be create
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async createFolder(path, folder_name) {
    let request = {
      op  : 'createFolder',
      data: JSON.stringify({
        folder: folder_name,
        path  : path,
      })
    }
    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Search for a given term on a given meo cloud path
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} term     search term
   * @param {String} path     search path
   * @param {String} criteria search criteria
   *
   * @returns {Promise<HTTPResponseEntity>}
   */
  async search(path, term, criteria) {
    let request = {
      op  : 'search',
      data: JSON.stringify({
        term    : term,
        path    : path,
        criteria: criteria,
      })
    }

    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Creates a public links for a given files
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Array}  files       files to be shared
   * @param {String} expiration  expiration date
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async shareFiles(files, expiration) {
    let request = {
      op  : 'processPublicLinks',
      data: JSON.stringify({
        items      : files,
        linkExpire : expiration,
      })
    }

    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Calls the backend api to sign a file upload
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} file_path file path
   * @param {String} file_name file name
   *
   * @return {Promise<HTTPResponseEntity>}
   */
  async signUpload(file_path, file_name) {
    let request = {
      op: 'signUploadCloud',
      data: JSON.stringify({
        file : {
          name: file_name,
        },
        path: file_path,
        type: 'normal',
      })
    }

    return await HTTPService.post(this.endpoint, request);
  }

  /**
   * Calls the backend api to uploads an attachment file to the server
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {EventUpload} event     event upload
   * @param {Object}      signature cloud signature
   *
   * @return {Promise<void>}
   */
  async uploadFile(event, signature) {
    let headers                 = {
      'Content-type': 'application/octet-stream',
      'Accept'      : '*/*'
    };


    let blob = new Blob([event.file], { type: event.file.type });
    await HTTPService.uploadBlob(signature.uri + '&' + signature.headers, event, blob, headers);
  }

  /**
   * Uploads an attachment to the cloud
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {MessageAttachmentsItem} attachment attachment file to be uploaded
   * @param {String}                 path       path to upload the file
   *
   * @return {Promise<void>}
   */
  async uploadAttachmentToCloud(attachment, file_path) {
    let request = {
      op  : 'uploadShortFile',
      data: JSON.stringify({
        id: attachment.id,
        file: attachment.name,
        muid: attachment.muid,
        path: file_path,
      })
    }

    return await HTTPService.post(this.endpoint, request);
  }
}
